import { LoaderCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useRudderstackConfigured } from '@/_shared/hooks/useRudderstackConfigured';
import { RudderstackNotConfiguredCard } from '@/settings/components/RudderstackNotConfiguredCard';

import { ChatConversation } from './ChatConversation';
import { ChatWelcome } from './ChatWelcome';

export function Chat(): React.ReactElement {
  const { id: sessionId } = useParams<{ id: string }>();
  const { wp } = useWorkspace();
  const { isConfigured, isLoading: settingsLoading, hasSettings } = useRudderstackConfigured();

  // Tri-state: `null` while the query hasn't resolved, `true` when configured,
  // `false` when the workspace is unconfigured.
  const hasRudderstack = hasSettings ? isConfigured : null;

  if (settingsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3" role="status" aria-label="Loading">
          <LoaderCircle size={18} className="animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      </div>
    );
  }

  // Setup is needed to start a new conversation, not to read an existing one.
  // ChatConversation still applies the chat's access and owner-only interaction rules.
  if (hasRudderstack === false && !sessionId) {
    return (
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl">
          <RudderstackNotConfiguredCard
            description="Rudder Lookout needs a connected RudderStack workspace before you can start a conversation. Sign in with RudderStack to link one — you can still open your chats in the meantime."
            secondaryAction={
              <Button asChild variant="secondary">
                <Link to={wp('/chats')}>Open chats</Link>
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-1 flex-col">
      {sessionId ? <ChatConversation sessionId={sessionId} /> : <ChatWelcome />}
    </div>
  );
}
